import { Injectable, OnDestroy } from "@angular/core";
import { Graphics } from "../digital-twin/graphics/graphics";
import { GraphicsMultipleSelection } from "../digital-twin/graphics/graphics-multiple-selection";
import { DTSelectionService } from "../digital-twin/selection/selection.service";
import { AssetManager } from "./asset-manager";
import { GraphicsRenderRuler } from "../digital-twin/graphics/graphics-render-ruler";
import * as THREE from '@phdsoft/three';
import { GraphicsMaterials } from "../digital-twin/graphics/graphics-materials";
import { WorkableElement } from "@app/platform/busines-data/SectionElements/WorkableElement";

@Injectable({ providedIn: 'root' })
export class AssetManagerTools implements OnDestroy {
  constructor(
    private graphics: Graphics,
    private graphicsMultipleSelection: GraphicsMultipleSelection,
    private graphicsRenderRuler: GraphicsRenderRuler,
    private assetManager: AssetManager,
    private selectionService: DTSelectionService
  ) { }

  private _isActive: boolean = false;

  public get isActive(): boolean {
    return this._isActive;
  }
  public set isActive(value: boolean) {
    this._isActive = value;
  }

  private _showedSaveAllConfirmation: boolean = true;

  private _temporaryGeometries: {
    positiveMesh: THREE.Mesh;
    negativeMesh: THREE.Mesh;
  } | null = null;

  public get temporaryGeometries() {
    return this._temporaryGeometries;
  }

  
  private _platesToMerge : Array<WorkableElement>;
  public get platesToMerge() : Array<WorkableElement> {
    return this._platesToMerge;
  }
  public set platesToMerge(v : Array<WorkableElement>) {
    this._platesToMerge = v;
  }
  

  public setTemporaryGeometries(positiveGeometry: THREE.BufferGeometry, negativeGeometry: THREE.BufferGeometry) {
    this.clearTemporaryGeometries();
    
    const positiveMesh = new THREE.Mesh(positiveGeometry, GraphicsMaterials.basicMaterial);
    const negativeMesh = new THREE.Mesh(negativeGeometry, GraphicsMaterials.basicMaterial);
    
    this._temporaryGeometries = {
      positiveMesh,
      negativeMesh
    };

    this.graphics.scene.add(positiveMesh);
    this.graphics.scene.add(negativeMesh);
  }

  public clearTemporaryGeometries() {
    if (this._temporaryGeometries) {
      this.graphics.scene.remove(this._temporaryGeometries.positiveMesh);
      this.graphics.scene.remove(this._temporaryGeometries.negativeMesh);
      this._temporaryGeometries = null;
    }
  }

  public get showedSaveAllConfirmation(): boolean {
    return this._showedSaveAllConfirmation;
  }
  public set showedSaveAllConfirmation(value: boolean) {
    this._showedSaveAllConfirmation = value;
  }

  public selectFirstElement() {
    if (this.assetManager.selectedInspections.length > 0) {
      this.selectionService.clearElementSelected(this.assetManager.currentSection?.Guid, this.assetManager.asset, false);
      this.selectionService.markElementSelected(this.assetManager.selectedInspections[0].Guid, this.assetManager.asset);
    }
  }

  public selectSecondaryElements() {
    if (this.assetManager.selectedInspections.length > 1) {
      let secElementIds = []
      for (let i = 1; i < this.assetManager.selectedInspections.length; i++) {
        secElementIds.push(this.assetManager.selectedInspections[i].Guid)
      }
      if (secElementIds.length > 0) {
        this.selectionService.markSecondaryElementsSelected(secElementIds, this.assetManager.asset)
      }
    }
  }

  public toggleMeasureTools() {
    if (this.assetManager.asset?.mode !== 'SECTIONS') {
      this.graphicsRenderRuler.clearGeometries();
      if (this.assetManager.functionalityMode === 'MEASURETOOLS') {
        this.assetManager.setFunctionalityModeNone();
      }
      return;
    }

    if (this.assetManager.functionalityMode === 'MEASURETOOLS') {
      if (this.graphics.Controls.enableRotate) {
        this.resumeMeasureTools();
      } else {
        this.deactivateMeasureTools(true);
      }
      return;
    }

    this.activateMeasureTools();
  }

  public toggleMeasureToolsRotation(): boolean {
    if (this.assetManager.functionalityMode !== 'MEASURETOOLS') {
      return false;
    }

    if (this.assetManager.asset?.mode !== 'SECTIONS') {
      this.deactivateMeasureTools(true);
      return true;
    }

    if (this.graphics.Controls.enableRotate) {
      this.resumeMeasureTools();
    } else {
      this.pauseMeasureTools();
    }
    return true;
  }

  public deactivateMeasureTools(clearRulers: boolean): void {
    if (clearRulers) {
      this.graphicsRenderRuler.clearGeometries();
    } else {
      this.graphicsRenderRuler.pauseDrawing();
    }

    if (this.assetManager.functionalityMode === 'MEASURETOOLS') {
      this.assetManager.setFunctionalityModeNone();
    } else {
      this.graphics.Controls.enableRotate = true;
      this.graphics.hammerManager.get('pan')?.set({ enable: false });
    }
  }

  private activateMeasureTools(): void {
    this.graphicsMultipleSelection.selectedFilter = null;
    this.resumeMeasureTools();
    this.assetManager.functionalityMode = 'MEASURETOOLS';
  }

  private pauseMeasureTools(): void {
    this.graphicsRenderRuler.pauseDrawing();
    this.graphics.Controls.enableRotate = true;
    this.graphics.Controls.enabled = true;
    this.graphics.hammerManager.get('pan')?.set({ enable: false });
  }

  private resumeMeasureTools(): void {
    this.graphicsMultipleSelection.selectedFilter = null;
    this.graphics.Controls.enableRotate = false;
    this.graphics.Controls.enabled = true;
    this.graphics.hammerManager.get('pan')?.set({ enable: true });
  }

  public toggleBuildStiffenerOrthogonal() {
    if (
     this.assetManager.functionalityMode !== 'STIFFNERINSERTION'
   ) {
     this.graphicsMultipleSelection.selectedFilter = null;
     this.graphics.Controls.enableRotate = false
     this.graphics.hammerManager.get('pan').set({ enable: true });
     //this.assetManager.enableMultipleSelection();
     this.assetManager.functionalityMode = 'STIFFNERINSERTION'
   } else {
     this.assetManager.setFunctionalityModeNone();
     //this.graphicsRenderRuler.clearGeometries();
   }
 }

  public toggleBuildCutPlate() {
    if (this.assetManager.functionalityMode !== 'CUTPLATE') {
      this.graphicsMultipleSelection.selectedFilter = null;
      this.graphics.Controls.enableRotate = false
      this.graphics.hammerManager.get('pan').set({ enable: true });
      this.assetManager.functionalityMode = 'CUTPLATE';
    } else {
      this.assetManager.setFunctionalityModeNone();
    }
  }

  public toggleBuildMergePlate() {
    if (this.assetManager.functionalityMode !== 'MERGEPLATE') {
      this.graphicsMultipleSelection.selectedFilter = null;
      this.graphics.Controls.enableRotate = false
      this.graphics.hammerManager.get('pan').set({ enable: true });
      this.assetManager.functionalityMode = 'MERGEPLATE';
    }
  }

  public initializeSelectionArea(): void {
    this.graphicsMultipleSelection.threeStartGeometryCloning();
    this.graphics.rectd.startRectangleSelection = true;
    this.showedSaveAllConfirmation = false;
  }

  public nextMeasurementInsertion(element): void {
    this.graphicsMultipleSelection.setNextSelectedPointActive(element);
  }

  public clearGeometries(){
    this.graphicsRenderRuler.clearGeometries();
  }
  ngOnDestroy(): void {
    throw new Error("Method not implemented.");
  }

}
